import type { JSX } from 'react';
import { useTranslation } from 'react-i18next';

import { isAppError, useErrorText } from '@/shared/api';

import { Button } from '../Button/Button';

interface ErrorStateProps {
  /**
   * The failure itself, when the caller has it. Preferred over `code`: an
   * `AppError` also carries the sentence the server already wrote, so a code
   * this app has no local message for still reads as a reason instead of
   * «مشکلی پیش آمد».
   */
  readonly error?: unknown;
  /** Backend machine code, for callers that only thread the code down (CLAUDE.md §7.4). */
  readonly code?: string;
  readonly onRetry?: () => void;
}

export function ErrorState({ error, code, onRetry }: ErrorStateProps): JSX.Element {
  const { t } = useTranslation(['errors', 'common']);
  const errorText = useErrorText();

  return (
    <div className="flex flex-col items-center gap-4 p-8 text-center" role="alert">
      <p className="text-base text-text">{text()}</p>
      {onRetry ? (
        <Button variant="primary" onClick={onRetry}>
          {t('actions.retry', { ns: 'common' })}
        </Button>
      ) : null}
    </div>
  );

  function text(): string {
    if (isAppError(error)) {
      return errorText(error);
    }

    return t(code ?? 'common.server_error', {
      ns: 'errors',
      defaultValue: t('errors.generic', { ns: 'common' }),
    });
  }
}
